import { getSignedMediaUrl } from '@/lib/utils';
import type { CartItemResponse, AddOnResponse, SelectedAddOn, ItemStatusCode } from '@/features/cart/types';

/**
 * Get add-on by ID from the provided addOns array
 */
export function getAddOnById(addOns: AddOnResponse[], addOnId: string): AddOnResponse | undefined {
  return addOns.find(addOn => addOn.id === addOnId);
}

/**
 * Get price for an add-on from the cart item's add_ons_per_guest
 * Returns the total price for this add-on for the guest (quantity × unit price)
 */
export function getAddOnPrice(cartItem: CartItemResponse, addOnId: string, guestIndex: number): number | null {
  const guestAddOns = cartItem.add_ons_per_guest[guestIndex];
  if (!guestAddOns) return null;
  const selectedAddOn = guestAddOns.find(ao => ao.add_on_id === addOnId);
  if (!selectedAddOn) return null;
  // Return quantity × price for total, or just price if quantity is not present (default to 1)
  const quantity = selectedAddOn.quantity || 1;
  return selectedAddOn.price * quantity;
}

/**
 * Get quantity for an add-on for a specific guest
 */
export function getAddOnQuantityForGuest(cartItem: CartItemResponse, addOnId: string, guestIndex: number): number {
  const guestAddOns = cartItem.add_ons_per_guest[guestIndex];
  if (!guestAddOns) return 0;
  const selectedAddOn = guestAddOns.find(ao => ao.add_on_id === addOnId);
  return selectedAddOn?.quantity || 0;
}

/**
 * Get add-on status from add-on statuses array
 */
export function getAddOnStatus(addOnStatuses: Array<{ add_on_id: string; status_code: ItemStatusCode }>, addOnId: string): ItemStatusCode | null {
  const status = addOnStatuses.find(s => s.add_on_id === addOnId);
  return status?.status_code || null;
}

/**
 * Get guests that have a specific add-on
 */
export function getGuestsWithAddOn(
  cartItem: CartItemResponse,
  addOnId: string,
  useEditData: boolean,
  editAddOnsPerGuest?: SelectedAddOn[][],
  removedItems?: Set<string>,
  removedGuests?: Set<number>
): number[] {
  const guests: number[] = [];
  const dataToUse = useEditData && editAddOnsPerGuest ? editAddOnsPerGuest : cartItem.add_ons_per_guest;
  if (!dataToUse) return guests;
  dataToUse.forEach((guestAddOns, index) => {
    if (!guestAddOns) return;
    if (guestAddOns.some(addOn => addOn.add_on_id === addOnId)) {
      // In edit mode, exclude removed items and removed guests
      if (useEditData) {
        const key = `${index}-${addOnId}`;
        if (removedItems?.has(key) || removedGuests?.has(index)) {
          return;
        }
      }
      guests.push(index + 1);
    }
  });
  return guests;
}

/**
 * Get event image URL
 */
export function getEventImage(
  event: { card_image_id?: string | null } | null | undefined,
  bookingCategory: { card_image_id?: string | null } | null | undefined
): string | null {
  if (event?.card_image_id && event.card_image_id !== '00000000-0000-0000-0000-000000000000') {
    return getSignedMediaUrl(event.card_image_id, 'image').url;
  }
  if (bookingCategory?.card_image_id && bookingCategory.card_image_id !== '00000000-0000-0000-0000-000000000000') {
    return getSignedMediaUrl(bookingCategory.card_image_id, 'image').url;
  }
  return null;
}

/**
 * Calculate price breakdown in edit mode
 */
export interface EditModePriceBreakdown {
  basePrice: number;
  addOnsPrice: number;
  platformFee: number;
  total: number;
  pricePerGuest: number;
  activeGuestCount: number;
}

export function calculateEditModePrice(
  cartItem: CartItemResponse,
  editGuestCount: number,
  editAddOnsPerGuest: SelectedAddOn[][],
  removedGuests: Set<number>,
  removedItems: Set<string>,
  isTableBooking: boolean = false,
  tableSize?: number | null
): EditModePriceBreakdown {
  // base_price is PER GUEST/SEAT
  // For table bookings: use table_size (full table capacity) for base price
  // For non-table bookings: use guest count
  const pricePerGuest = cartItem.base_price;
  const activeGuestCount = editGuestCount;
  const basePriceMultiplier = isTableBooking && tableSize ? tableSize : activeGuestCount;
  const basePrice = pricePerGuest * basePriceMultiplier;

  // Calculate add-ons price for active guests only (excluding removed items)
  let addOnsPrice = 0;
  if (editAddOnsPerGuest) {
    editAddOnsPerGuest.forEach((guestAddOns, guestIndex) => {
      if (!guestAddOns || removedGuests.has(guestIndex)) return;
      guestAddOns.forEach(addOn => {
        const key = `${guestIndex}-${addOn.add_on_id}`;
        if (!removedItems.has(key)) {
          // Multiply price by quantity for total add-on price
          const quantity = addOn.quantity || 1;
          addOnsPrice += addOn.price * quantity;
        }
      });
    });
  }

  // Platform fee: for table bookings, recalculate based on table size
  // For non-table bookings: scale based on guest count
  const platformFeePerGuest = cartItem.platform_fee / cartItem.total_guests;
  const platformFee = platformFeePerGuest * (isTableBooking && tableSize ? tableSize : activeGuestCount);
  const total = basePrice + addOnsPrice + platformFee;

  return {
    basePrice,
    addOnsPrice,
    platformFee,
    total,
    pricePerGuest,
    activeGuestCount,
  };
}

